"use client";

import { toast } from "@workspace/ui/components/sonner";
import {
  getFetchRequestHeaders,
  isStaleClientError,
  isStaleClientReason,
  isStaleServerActionResponse,
  NEXT_ACTION_HEADER,
} from "@/lib/stale-client";

export const STALE_CLIENT_RELOAD_MESSAGE =
  "Hay una versión nueva. Recargá la página.";
export const STALE_CLIENT_TOAST_ID = "stale-client";
export const STALE_CLIENT_RELOAD_ACTION_LABEL = "Recargar";

type ToastPromise = typeof toast.promise;
type ToastError = typeof toast.error;
type ToastDismiss = typeof toast.dismiss;

let installCount = 0;
let originalPromise: ToastPromise | null = null;
let originalError: ToastError | null = null;
let originalDismiss: ToastDismiss | null = null;
let originalFetch: typeof fetch | null = null;

function reloadPage() {
  window.location.reload();
}

export function getStaleClientReloadToastData() {
  return {
    action: {
      label: STALE_CLIENT_RELOAD_ACTION_LABEL,
      onClick: reloadPage,
    },
    duration: Number.POSITIVE_INFINITY,
    id: STALE_CLIENT_TOAST_ID,
    message: STALE_CLIENT_RELOAD_MESSAGE,
  };
}

export function showStaleClientReloadToast() {
  const errorFn = originalError ?? toast.error.bind(toast);
  errorFn(STALE_CLIENT_RELOAD_MESSAGE, {
    action: {
      label: STALE_CLIENT_RELOAD_ACTION_LABEL,
      onClick: reloadPage,
    },
    duration: Number.POSITIVE_INFINITY,
    id: STALE_CLIENT_TOAST_ID,
  });
}

export function handleStaleClientError(reason: unknown): boolean {
  if (!isStaleClientReason(reason)) {
    return false;
  }

  showStaleClientReloadToast();
  return true;
}

function getToastPromiseId(
  result: ReturnType<ToastPromise>
): string | number | undefined {
  if (typeof result === "string" || typeof result === "number") {
    return result;
  }

  if (typeof result === "object" && result !== null && "valueOf" in result) {
    const value = result.valueOf();
    if (typeof value === "string" || typeof value === "number") {
      return value;
    }
  }

  return undefined;
}

function extractToastMessage(message: unknown): string | undefined {
  return typeof message === "string" ? message : undefined;
}

function installToastHandlers() {
  if (originalPromise && originalError && originalDismiss) {
    return;
  }

  const promiseImpl = toast.promise.bind(toast);
  const errorImpl = toast.error.bind(toast);
  const dismissImpl = toast.dismiss.bind(toast);

  originalPromise = promiseImpl;
  originalError = errorImpl;
  originalDismiss = dismissImpl;

  toast.error = ((message, data) => {
    const text = extractToastMessage(message);
    if (text && isStaleClientError(text)) {
      return errorImpl(STALE_CLIENT_RELOAD_MESSAGE, {
        ...data,
        action: {
          label: STALE_CLIENT_RELOAD_ACTION_LABEL,
          onClick: reloadPage,
        },
        duration: Number.POSITIVE_INFINITY,
        id: data?.id ?? STALE_CLIENT_TOAST_ID,
      });
    }

    return errorImpl(message, data);
  }) as ToastError;

  toast.promise = ((promise, data) => {
    if (!data) {
      return promiseImpl(promise, data);
    }

    const originalErrorHandler = data.error;
    let toastResult!: ReturnType<ToastPromise>;

    if (originalErrorHandler === undefined) {
      toastResult = promiseImpl(
        Promise.resolve(
          typeof promise === "function" ? promise() : promise
        ).catch((error: unknown) => {
          if (isStaleClientReason(error)) {
            showStaleClientReloadToast();
          }
          throw error;
        }),
        data
      );
      return toastResult;
    }

    toastResult = promiseImpl(promise, {
      ...data,
      error: ((error: unknown) => {
        if (isStaleClientReason(error)) {
          const loadingId = getToastPromiseId(toastResult);
          if (loadingId != null && loadingId !== STALE_CLIENT_TOAST_ID) {
            dismissImpl(loadingId);
          }
          showStaleClientReloadToast();
          return getStaleClientReloadToastData();
        }

        if (typeof originalErrorHandler === "function") {
          return originalErrorHandler(error);
        }

        return originalErrorHandler;
      }) as NonNullable<(typeof data)["error"]>,
    });

    return toastResult;
  }) as ToastPromise;
}

function installActionTransportHandler() {
  if (originalFetch || typeof window === "undefined") {
    return;
  }

  const fetchImpl = window.fetch.bind(window);
  originalFetch = fetchImpl;

  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    const requestHeaders = getFetchRequestHeaders(input, init);

    return Promise.resolve(fetchImpl(input, init)).then(
      (response) => {
        if (isStaleServerActionResponse(requestHeaders, response)) {
          showStaleClientReloadToast();
        }
        return response;
      },
      (error: unknown) => {
        if (
          requestHeaders.has(NEXT_ACTION_HEADER) &&
          isStaleClientReason(error)
        ) {
          showStaleClientReloadToast();
        }
        throw error;
      }
    );
  }) as typeof fetch;
}

function restoreToastHandlers() {
  if (originalPromise) {
    toast.promise = originalPromise;
    originalPromise = null;
  }
  if (originalError) {
    toast.error = originalError;
    originalError = null;
  }
  if (originalDismiss) {
    toast.dismiss = originalDismiss;
    originalDismiss = null;
  }
}

function restoreActionTransportHandler() {
  if (originalFetch) {
    window.fetch = originalFetch;
    originalFetch = null;
  }
}

export function installStaleClientHandledErrorHandlers(): () => void {
  installCount += 1;
  if (installCount === 1) {
    installToastHandlers();
    installActionTransportHandler();
  }

  return () => {
    installCount = Math.max(0, installCount - 1);
    if (installCount === 0) {
      restoreToastHandlers();
      restoreActionTransportHandler();
    }
  };
}
